<template>
  <div class="stopwatch">
    <div class="timer-display">
      <span>{{ formattedTime }}</span>
      <div class="controls">
        <button
          @click="handleTogglePause"
          class="control-btn"
          :title="isPaused ? 'Resume' : 'Pause'"
        >
          <span class="material-icons">{{
            isPaused ? 'play_arrow' : 'pause'
          }}</span>
        </button>
        <button @click="handleStop" class="control-btn" title="Stop">
          <span class="material-icons">stop</span>
        </button>
      </div>
    </div>
    <div class="progress-bar-container">
      <div class="progress-bar" :style="progressBarStyle"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StopWatch',
  emits: ['toggle-pause', 'stop'],
  props: {
    // The total time in milliseconds already elapsed from previous sessions.
    totalElapsed: {
      type: Number,
      default: 0,
    },
    // The timestamp when the timer was last (re)started.
    startTime: {
      type: Number,
      default: () => Date.now(),
    },
    // Whether the timer is currently paused.
    isPaused: {
      type: Boolean,
      default: false,
    },
    progressBarConfig: {
      type: Object,
      default: () => ({
        maxTime: 40 * 60 * 1000, // 40 minutes in ms
        colorThresholds: [
          // 0-15 minutes: green
          { time: 15 * 60 * 1000, color: '#2dce89' },
          // 15-25 minutes: yellow
          { time: 25 * 60 * 1000, color: '#ffd600' },
          // 25+ minutes: red
          { time: Infinity, color: '#ed4245' },
        ],
      }),
    },
  },
  data() {
    return {
      intervalId: null,
      // A reactive property to force re-computation of elapsed time.
      now: Date.now(),
    }
  },
  computed: {
    currentElapsedTime() {
      if (this.isPaused) {
        return this.totalElapsed
      }
      // Current running session's elapsed time + previously accumulated time.
      return this.now - this.startTime + this.totalElapsed
    },
    formattedTime() {
      const totalSeconds = Math.floor(this.currentElapsedTime / 1000)
      const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
      const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
        2,
        '0'
      )
      const seconds = String(totalSeconds % 60).padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    },
    progressPercentage() {
      const maxTime = this.progressBarConfig.maxTime
      const percentage = (this.currentElapsedTime / maxTime) * 100
      return Math.min(percentage, 100)
    },
    progressBarColor() {
      const thresholds = this.progressBarConfig.colorThresholds
      for (const threshold of thresholds) {
        if (this.currentElapsedTime <= threshold.time) {
          return threshold.color
        }
      }
      return thresholds[thresholds.length - 1].color
    },
    progressBarStyle() {
      return {
        width: `${this.progressPercentage}%`,
        backgroundColor: this.progressBarColor,
      }
    },
  },
  methods: {
    handleTogglePause() {
      this.$emit('toggle-pause')
    },
    handleStop() {
      this.$emit('stop')
    },
  },
  mounted() {
    this.intervalId = setInterval(() => {
      if (!this.isPaused) {
        this.now = Date.now()
      }
    }, 1000)
  },
  beforeUnmount() {
    clearInterval(this.intervalId)
  },
}
</script>
<style scoped>
.stopwatch {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  font-size: 14px;
  color: #b9bbbe; /* Lighter grey for text */
  margin-top: 8px;
  gap: 8px;
}

.timer-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.progress-bar-container {
  background-color: #2f3136; /* Dark background for progress bar */
  border-radius: 4px;
  height: 8px;
  width: 100%;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s linear, background-color 0.5s ease;
}

.controls {
  display: flex;
  gap: 8px;
}

.control-btn {
  background: none;
  border: 1px solid #4f545c; /* Darker border */
  border-radius: 50%;
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #b9bbbe; /* Lighter icon color */
  transition: background-color 0.2s;
}

.control-btn:hover {
  background-color: #4f545c; /* Darker hover background */
}

.material-icons {
  font-size: 20px;
}

@media (max-width: 768px) {
  .control-btn {
    /* Increase size for better touch targets */
    width: 36px;
    height: 36px;
  }

  .material-icons {
    font-size: 24px;
  }
}
</style>
